import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { LogOut, Youtube, ExternalLink, Award, Trophy, Globe, GraduationCap, PlayCircle, Heart } from 'lucide-react';
import { TCSLogo } from '../components/TCSLogo';
import { NavArrows } from '../components/NavArrows';

const YOUTUBE_URL = 'https://www.youtube.com/@THYAGOTCS';

const CHANNEL_TOPICS = [
  'Treinos de ciclismo de alto desempenho',
  'Fisiologia aplicada ao esporte',
  'Nutrição e suplementação para atletas',
  'Saúde cardíaca e prevenção',
  'Análise de coleta fisiológica',
  'Periodização e ganho de performance',
  'Dúvidas reais de alunos respondidas em vídeo',
];

const CREDENTIALS = [
  { icon: Trophy, label: 'Atleta profissional há 17 anos' },
  { icon: Award, label: '7 títulos nacionais conquistados' },
  { icon: Globe, label: 'Múltiplos títulos internacionais' },
  { icon: PlayCircle, label: 'Participante de Mundiais e Pan-Americanos' },
  { icon: GraduationCap, label: 'Pós-graduação em Fisiologia e Ciência do Esporte' },
  { icon: Heart, label: 'Especialização Internacional em Coleta Fisiológica' },
];

const Channel = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-xl sticky top-0 z-20">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <NavArrows />
            <TCSLogo size="sm" showTagline={false} />
            <div className="hidden sm:block">
              <h1 className="text-lg md:text-xl font-black uppercase tracking-tight text-[#FF0000]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Canal YT
              </h1>
              <p className="text-[10px] md:text-xs tracking-[0.2em] uppercase text-[#A1A1AA]">Conteúdo exclusivo do treinador</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            size="icon"
            variant="outline"
            className="bg-transparent border-[#FF3366] text-[#FF3366] hover:bg-[#FF3366]/10 rounded-none flex-shrink-0"
            data-testid="channel-logout-button"
            aria-label="Sair"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 md:py-10 max-w-3xl" data-testid="channel-main">
        {/* Hero */}
        <div className="bg-gradient-to-br from-[#FF0000]/10 via-transparent to-[#00F0FF]/5 border border-[#FF0000]/30 p-6 md:p-8 mb-8">
          <div className="flex items-start gap-3 mb-4">
            <Youtube className="h-8 w-8 md:h-10 md:w-10 text-[#FF0000] flex-shrink-0" />
            <div className="flex-1">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF0000] mb-1">YouTube · Thyago TCS</p>
              <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-white leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Conteúdo gratuito para sua evolução
              </h2>
            </div>
          </div>
          <p className="text-sm md:text-base text-white/90 leading-relaxed mb-6">
            No canal oficial do treinador e fisiologista esportivo <strong className="text-[#00F0FF]">Thyago Costa</strong>, você encontra um acervo de vídeos com informações técnicas sobre treinamento, fisiologia, nutrição, saúde cardíaca e performance esportiva. Conteúdo gratuito, atualizado constantemente e construído a partir de dúvidas reais de atletas.
          </p>
          <a
            href={YOUTUBE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="channel-youtube-link"
            className="inline-flex items-center gap-2 bg-[#FF0000] hover:bg-[#CC0000] text-white font-bold uppercase tracking-wider px-6 py-3 transition-colors"
          >
            <Youtube className="h-5 w-5" />
            Acessar canal no YouTube
            <ExternalLink className="h-4 w-4 opacity-70" />
          </a>
        </div>

        {/* Sobre Thyago */}
        <section className="mb-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-3">Sobre o treinador</p>
          <h3 className="text-xl md:text-2xl font-black uppercase tracking-tight text-white mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Thyago Costa Silva
          </h3>
          <p className="text-sm md:text-base text-white/90 leading-relaxed mb-6">
            Atleta profissional, fisiologista esportivo e treinador. Há 17 anos no alto rendimento, alia experiência competitiva internacional a sólida formação acadêmica em fisiologia aplicada ao esporte. Combina prática e ciência para entregar treinamento personalizado e baseado em evidência aos seus atletas.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {CREDENTIALS.map((c, idx) => {
              const Icon = c.icon;
              return (
                <div
                  key={idx}
                  className="flex items-center gap-3 bg-[#121212] border border-white/10 px-4 py-3"
                  data-testid={`credential-${idx}`}
                >
                  <Icon className="h-4 w-4 text-[#00F0FF] flex-shrink-0" />
                  <span className="text-sm text-white">{c.label}</span>
                </div>
              );
            })}
          </div>
        </section>

        {/* O que vai encontrar */}
        <section className="mb-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A1A1AA] mb-3">O que você encontra no canal</p>
          <h3 className="text-xl md:text-2xl font-black uppercase tracking-tight text-white mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Temas abordados
          </h3>
          <ul className="space-y-2">
            {CHANNEL_TOPICS.map((topic, idx) => (
              <li
                key={idx}
                className="flex items-start gap-3 bg-[#121212] border border-white/10 px-4 py-3"
              >
                <PlayCircle className="h-4 w-4 text-[#FF0000] flex-shrink-0 mt-0.5" />
                <span className="text-sm md:text-base text-white">{topic}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* CTA final */}
        <div className="bg-[#00F0FF]/5 border border-[#00F0FF]/30 p-5 md:p-6">
          <div className="flex items-start gap-3 mb-4">
            <Heart className="h-5 w-5 text-[#00F0FF] flex-shrink-0 mt-0.5" fill="#00F0FF" />
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#00F0FF] mb-1">Lembrete TCS</p>
              <p className="text-sm md:text-base text-white leading-relaxed">
                Antes de tirar uma dúvida, busque pelo seu tema no canal. Provavelmente já existe um vídeo explicando exatamente o que você precisa — em detalhe, com fundamentação científica e linguagem direta.
              </p>
            </div>
          </div>
          <a
            href={YOUTUBE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="channel-youtube-link-bottom"
            className="inline-flex items-center gap-2 bg-[#FF0000] hover:bg-[#CC0000] text-white font-bold uppercase tracking-wider px-5 py-2.5 text-sm transition-colors"
          >
            <Youtube className="h-4 w-4" />
            Inscreva-se no canal
            <ExternalLink className="h-3 w-3 opacity-70" />
          </a>
        </div>
      </main>
    </div>
  );
};

export default Channel;
